import { createAsyncThunk, createSelector, createSlice, miniSerializeError } from '@reduxjs/toolkit';
import axios from 'axios';
import {
    getAuth,
    sendPasswordResetEmail,
    signOut,
} from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { authUtils } from '../utils';

// SELECTORS-----------------------------------------
const currentAuthenticatedUser = createSelector(
    state => state.auth.user,
    user => user?.email ? user : null,
);

const currentSelectedTenant = createSelector(
    state => state.auth.currentSelectedTenant,
    tenant => tenant,
);

const availableTenants = createSelector(
    state => state.auth.user.tenants,
    tenants => tenants?.length ? tenants : null,
);

const authLoading = createSelector(
    state => state.auth.isLoading,
    isLoading => Object.values(isLoading).some(val => val),
);

const isFNIAdmin = createSelector(
    state => state.auth?.user?.isFNIAdmin,
    isAdmin => isAdmin,
);

const canAccessTenantAdmin = createSelector(
    state => state.auth?.user?.isFNIAdmin,
    state => state.auth?.user?.canAccessTenantAdmin,
    (isFNIAdmin, canAccess) => isFNIAdmin || canAccess,
);

const authAlert = createSelector(
    state => state.auth.alert,
    alert => alert,
);

const isUserRegistered = createSelector(
    state => state.auth.userIsRegistered,
    isRegistered => isRegistered,
);

const tenantIsMigrated = createSelector(
    state => state.auth.tenantIsMigrated,
    isMigrated => isMigrated,
);

const mfaState = createSelector(
    state => state.auth.mfa,
    mfa => mfa,
)

const promptPasskeySetup = createSelector(
    state => state.auth.promptPasskeySetup,
    prompt => prompt,
)

const pendingRedirectUrl = createSelector(
    state => state.auth.pendingRedirectUrl,
    url => url,
)

const passkeyEnrollmentOptional = createSelector(
    state => state.auth.passkeyEnrollmentOptional,
    optional => optional,
)

// Indicates if auth state has been loaded (user checked)
const authReady = createSelector(
    state => state.auth.authReady,
    ready => ready,
)

const availableMfaTypes = createSelector(
    state => state.auth.mfa.availableTypes,
    types => types,
)

export const authSelectors = {
    authLoading,
    authAlert,
    authReady,
    availableTenants,
    isFNIAdmin,
    canAccessTenantAdmin,
    currentAuthenticatedUser,
    currentSelectedTenant,
    isUserRegistered,
    mfaState,
    promptPasskeySetup,
    pendingRedirectUrl,
    passkeyEnrollmentOptional,
    tenantIsMigrated,
    availableMfaTypes,
};

// ACTIONS --------------------------------------------
const forceChangePassword = createAsyncThunk(
    'auth/disableForceChangePassword',
    async (passwords) => {
        const functions = getFunctions();
        const forceChangePwdFunction = httpsCallable(functions, 'auth-forceChangePassword_v2');
        return await forceChangePwdFunction(passwords);
    },
);

const enrollMFA = createAsyncThunk(
    'auth/enrollMFA',
    async (data, { getState }) => {
        const mfaType = getState().auth.mfa.mfaType;
        const functions = getFunctions();
        const handleEnrollMFAFunction = httpsCallable(functions, 'auth-enrollMFA_v2');
        return await handleEnrollMFAFunction({ mfaType, data });
    },
);

const getAvailableMfaTypes = createAsyncThunk(
    'auth/getAvailableMfaTypes',
    async () => {
        const functions = getFunctions();
        const getAvailableMfaTypesFunction = httpsCallable(functions, 'auth-getAvailableMfaTypes_v2');
        return await getAvailableMfaTypesFunction();
    },
);

const enrollPasskey = createAsyncThunk(
    'auth/enrollPasskey',
    async () => {
        const functions = getFunctions();
        const enrollPasskeyFunction = httpsCallable(functions, 'auth-enrollPasskey_v2');
        return await enrollPasskeyFunction();
    },
);

const verifyPasskeyEnrollment = createAsyncThunk(
    'auth/verifyPasskeyEnrollment',
    async (data) => {
        const functions = getFunctions();
        const verifyPasskeyEnrollmentFunction = httpsCallable(functions, 'auth-verifyPasskeyEnrollment_v2');
        return await verifyPasskeyEnrollmentFunction(data);
    },
);

const generateAuthenticationOptions = createAsyncThunk(
    'auth/generateAuthenticationOptions',
    async () => {
        const functions = getFunctions();
        const generateAuthenticationOptionsFunction = httpsCallable(functions, 'auth-generateAuthenticationOptions_v2');
        return await generateAuthenticationOptionsFunction();
    },
);

const verifyAuthentication = createAsyncThunk(
    'auth/verifyAuthentication',
    async (data) => {
        const functions = getFunctions();
        const verifyAuthenticationFunction = httpsCallable(functions, 'auth-verifyAuthentication_v2');
        return await verifyAuthenticationFunction(data);
    },
);

const skipPasskeySetup = createAsyncThunk(
    'auth/skipPasskeySetup',
    async () => {
        const functions = getFunctions();
        const skipPasskeySetupFunction = httpsCallable(functions, 'auth-skipPasskeySetup_v2');
        return await skipPasskeySetupFunction();
    },
);

const getRedirect = createAsyncThunk(
    'auth/getRedirect',
    async (tenantCode) => {
        const functions = getFunctions();
        const getRedirectFunction = httpsCallable(functions, 'auth-getRedirect_v2');
        return await getRedirectFunction(tenantCode);
    },
);

const getCurrentTenantByCode = createAsyncThunk(
    'auth/getCurrentTenantByCode',
    async (_, { getState }) => {
        const code = getState().core.tenantCode;
        const functions = getFunctions();
        const getCurrentTenantByCodeFunction = httpsCallable(functions, 'tenants-getTenantByCode_v2');
        return await getCurrentTenantByCodeFunction(code);
    },
);

const handleInvite = createAsyncThunk(
    'auth/handleInvite',
    async (inviteId) => {
        const functions = getFunctions();
        const handleInviteFunction = httpsCallable(functions, 'users-handleUserInvite');
        return await handleInviteFunction(inviteId);
    },
);

const signInUser = createAsyncThunk(
    'auth/signInUser',
    async (creds) => {
        const url = '/loginWithCredentials';
        axios.defaults.headers.post['Content-Type'] = 'application/json;charset=utf-8';
        return await axios.post(url, creds, { withCredentials: true });
    },
    {
        serializeError: (payload) => {

            var customError = payload.response.data;
            switch (customError.message) {
                case 'MFA_NOT_ENABLED':
                case 'FORCE_CHANGE_PASSWORD':
                    return {
                        code: customError.code,
                        message: customError.message,
                        token: customError.details.token
                    };
                case 'MFA_CODE_REQUIRED':
                    return {
                        code: customError.code,
                        message: customError.message,
                        mfaType: customError.details.mfaType,
                        recipient: customError.details.to,
                        token: customError.details.token,
                        hasPasskey: customError.details.hasPasskey,
                        hasTotp: customError.details.hasTotp,
                        hasEmail: customError.details.hasEmail,
                    }
                default:
                    return miniSerializeError(customError);
            }
        }
    }
);

const signOutUser = createAsyncThunk(
    'auth/signOutUser',
    async (_, { dispatch, getState }) => {
        const code = getState().core?.tenantCode;
        await signOut(getAuth());
        dispatch(authActions.logout());
        return code;
    },
);

// NVT-41: voluntary in-app password change goes through the backend (the
// callable performs the GIP update AND emits the audit event atomically), so
// the change and its audit record are captured together server-side rather
// than via a best-effort client report. Replaces the old client-side
// updatePassword() call.
const changePassword = createAsyncThunk(
    'auth/changePassword',
    async ({ newPassword, tenantCode }) => {
        const functions = getFunctions();
        const fn = httpsCallable(functions, 'auth-changePassword_v2');
        return await fn({ newPassword, tenantCode });
    },
);

// NVT-41: fire-and-forget audit notification for the reset-via-email flow,
// which happens entirely client-side via the Firebase Auth SDK (the oobCode
// can only be redeemed by the client SDK, so the backend can't mediate it).
// Never throws — an audit failure must not disrupt the reset flow. `type` is
// RESET_REQUESTED or RESET_COMPLETED (see auth-recordPasswordEvent_v2).
const recordPasswordEvent = async ({ type, email, tenantCode }) => {
    try {
        const functions = getFunctions();
        const fn = httpsCallable(functions, 'auth-recordPasswordEvent_v2');
        await fn({ type, email, tenantCode });
    } catch (error) {
        // Swallow — audit is best-effort; the password operation already
        // succeeded by the time we get here.
        console.warn('Failed to record password audit event', error);
    }
};

const sendResetEmail = createAsyncThunk(
    'auth/sendResetEmail',
    async ({ email }, { getState }) => {
        const auth = getAuth();
        const tenantCode = getState().core.tenantCode;

        // Configure action code settings to use our custom handler
        const actionCodeSettings = {
            // URL must point to our custom email action handler
            url: `${window.location.origin}/auth/email-action`,
            handleCodeInApp: false, // We handle it in our custom page
        };

        if (tenantCode) {
            actionCodeSettings.url = `${window.location.origin}/auth/email-action?tid=${tenantCode}`;
        }

        const result = await sendPasswordResetEmail(auth, email, actionCodeSettings);
        // NVT-41: audit the reset request (unauthenticated forgot-password flow)
        recordPasswordEvent({ type: 'RESET_REQUESTED', email, tenantCode });
        return result;
    },
);

const verifyMFA = createAsyncThunk(
    'auth/verifyMFA',
    async (code) => {
        const functions = getFunctions();
        const verifyMFAFunction = httpsCallable(functions, 'auth-verifyMFA_v2');
        return await verifyMFAFunction(code);
    },
);

const getMfaAvailability = createAsyncThunk(
    'auth/getMfaAvailability',
    async () => {
        const functions = getFunctions();
        const getMfaAvailabilityFunction = httpsCallable(functions, 'auth-getMfaAvailability_v2');
        return await getMfaAvailabilityFunction();
    },
);

const getMfaSettings = createAsyncThunk(
    'auth/getMfaSettings',
    async () => {
        const functions = getFunctions();
        const getMfaSettingsFunction = httpsCallable(functions, 'auth-getMfaSettings_v2');
        return await getMfaSettingsFunction();
    },
);

const verifyMfaForSettings = createAsyncThunk(
    'auth/verifyMfaForSettings',
    async (mfaCode) => {
        const functions = getFunctions();
        const verifyMfaForSettingsFunction = httpsCallable(functions, 'auth-verifyMfaForSettings_v2');
        return await verifyMfaForSettingsFunction({ mfaCode });
    },
);

const sendEmailMfaForSettings = createAsyncThunk(
    'auth/sendEmailMfaForSettings',
    async () => {
        const functions = getFunctions();
        const sendEmailMfaForSettingsFunction = httpsCallable(functions, 'auth-sendEmailMfaForSettings_v2');
        return await sendEmailMfaForSettingsFunction();
    },
);

const removeTOTP = createAsyncThunk(
    'auth/removeTOTP',
    async () => {
        const functions = getFunctions();
        const removeTOTPFunction = httpsCallable(functions, 'auth-removeTOTP_v2');
        return await removeTOTPFunction();
    },
);

const removePasskey = createAsyncThunk(
    'auth/removePasskey',
    async (credentialId) => {
        const functions = getFunctions();
        const removePasskeyFunction = httpsCallable(functions, 'auth-removePasskey_v2');
        return await removePasskeyFunction({ credentialId });
    },
);

const verifySamlMfa = createAsyncThunk(
    'auth/verifySamlMfa',
    async ({ token, tenantCode, mfaCode, mfaType, remember, fingerprint }) => {
        const functions = getFunctions();
        const verifySamlMfaFunction = httpsCallable(functions, 'saml-verifySamlMfa_v2');
        return await verifySamlMfaFunction({ token, tenantCode, mfaCode, mfaType, remember, fingerprint });
    },
);

const removeEmailMFA = createAsyncThunk(
    'auth/removeEmailMFA',
    async () => {
        const functions = getFunctions();
        const removeEmailMFAFunction = httpsCallable(functions, 'auth-removeEmailMFA_v2');
        return await removeEmailMFAFunction();
    },
);

const setMfaType = (state, action) => {
    state.mfa.mfaType = action.payload;
}

const setMfaRecipient = (state, action) => {
    state.mfa.recipient = action.payload;
}

const setMfaState = (state, action) => {
    state.mfa = { ...state.mfa, ...action.payload };
}

const resetMfaState = (state) => {
    state.mfa = mfaInitialState;
}

const {
    alertInitialState,
    setAlertError,
    setAlertSuccess,
    trimActionType,
} = authUtils;

const mfaInitialState = {
    mfaType: null,
    recipient: '',
    hasPasskey: false,
    hasTotp: false,
    hasEmail: false,
    availableTypes: {
        totp: true,
        passkey: true,
        email: false,
    },
    modal: {
        verifyOpen: false,
        successOpen: false,
    },
};

const initialState = {
    user: {
        id: null,
        email: null,
        userId: null,
        tenantRoles: [],
        isFNIAdmin: null,
        canAccessTenantAdmin: null,
    },
    currentSelectedTenant: null,
    tenantIsMigrated: false,
    userIsRegistered: false,
    unregisteredUser: null,
    isLoading: {},
    alert: alertInitialState,
    mfa: mfaInitialState,
    promptPasskeySetup: false,
    pendingRedirectUrl: sessionStorage.getItem('pendingRedirectUrl') || null,
    passkeyEnrollmentOptional: sessionStorage.getItem('passkeyEnrollmentOptional') === 'true',
    authReady: false, // Set to true once initial auth check completes
};

export const AuthSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setCurrentUser(state, action) {
            state.user = action.payload;
        },
        clearAuth() {
            return initialState;
        },
        setSuccessMessage(state, action) {
            setAlertSuccess(state, action.payload);
        },
        setCurrentTenant(state, action) {
            state.currentSelectedTenant = action.payload;
        },
        setErrorMessage(state, action) {
            setAlertError(state, action.payload);
        },
        setTenantIsMigrated(state, action) {
            state.tenantIsMigrated = action.payload;
        },
        setUserIsRegistered(state, action) {
            state.userIsRegistered = action.payload;
        },
        hideAlert(state) {
            state.alert.open = false;
        },
        setAlert(state, action) {
            state.alert = {
                open: true,
                type: action.payload.type || 'error',
                message: action.payload.message || 'An error occurred',
            };
        },
        openMfaSuccessModal(state, action) {
            state.mfa.modal.successOpen = action.payload;
        },
        openMfaVerifyModal(state, action) {
            state.mfa.modal.verifyOpen = action.payload;
        },
        setPromptPasskeySetup(state, action) {
            state.promptPasskeySetup = action.payload;
        },
        setPendingRedirectUrl(state, action) {
            state.pendingRedirectUrl = action.payload;
            // Sync with sessionStorage
            if (action.payload) {
                sessionStorage.setItem('pendingRedirectUrl', action.payload);
            } else {
                sessionStorage.removeItem('pendingRedirectUrl');
            }
        },
        setPasskeyEnrollmentOptional(state, action) {
            state.passkeyEnrollmentOptional = action.payload;
            // Sync with sessionStorage
            if (action.payload) {
                sessionStorage.setItem('passkeyEnrollmentOptional', 'true');
            } else {
                sessionStorage.removeItem('passkeyEnrollmentOptional');
            }
        },
        setAuthReady(state, action) {
            state.authReady = action.payload;
        },
        setMfaType,
        setMfaState,
        resetMfaState,
        logout() {
            //check in store.js to see state reset for logout
        },
    },
    extraReducers: {
        [forceChangePassword.fulfilled]: (state, action) => {
            setAlertSuccess(state, 'Password changed!');
            state.isLoading[trimActionType(action.type)] = false;
        },
        [forceChangePassword.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [forceChangePassword.rejected]: (state, action) => {
            setAlertError(state, 'Unable to change password');
            state.isLoading[trimActionType(action.type)] = false;
        },
        [getCurrentTenantByCode.fulfilled]: (state, action) => {
            state.currentSelectedTenant = action.payload.data.tenantId;
            state.isLoading[trimActionType(action.type)] = false;
        },
        [getCurrentTenantByCode.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [getCurrentTenantByCode.rejected]: (state, action) => {
            setAlertError(state, 'Unable to get selected tenant');
            state.isLoading[trimActionType(action.type)] = false;
        },
        [enrollMFA.fulfilled]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
        },
        [enrollMFA.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [enrollMFA.rejected]: (state, action) => {
            setAlertError(state, 'Unable to enroll in MFA');
            state.isLoading[trimActionType(action.type)] = false;
        },
        [enrollPasskey.fulfilled]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
        },
        [enrollPasskey.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [enrollPasskey.rejected]: (state, action) => {
            setAlertError(state, 'Unable to enroll passkey');
            state.isLoading[trimActionType(action.type)] = false;
        },
        [verifyPasskeyEnrollment.fulfilled]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
        },
        [verifyPasskeyEnrollment.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [verifyPasskeyEnrollment.rejected]: (state, action) => {
            setAlertError(state, 'Unable to verify passkey enrollment');
            state.isLoading[trimActionType(action.type)] = false;
        },
        [generateAuthenticationOptions.fulfilled]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
        },
        [generateAuthenticationOptions.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [generateAuthenticationOptions.rejected]: (state, action) => {
            setAlertError(state, 'Unable to generate authentication options');
            state.isLoading[trimActionType(action.type)] = false;
        },
        [verifyAuthentication.fulfilled]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
        },
        [verifyAuthentication.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [verifyAuthentication.rejected]: (state, action) => {
            setAlertError(state, 'Unable to verify authentication');
            state.isLoading[trimActionType(action.type)] = false;
        },
        [skipPasskeySetup.fulfilled]: (state, action) => {
            state.promptPasskeySetup = false;
            state.isLoading[trimActionType(action.type)] = false;
        },
        [skipPasskeySetup.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [skipPasskeySetup.rejected]: (state, action) => {
            setAlertError(state, 'Unable to skip passkey setup');
            state.isLoading[trimActionType(action.type)] = false;
        },
        [getRedirect.fulfilled]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
        },
        [getRedirect.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [getRedirect.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
        },
        [handleInvite.fulfilled]: (state, action) => {
            state.unregisteredUser = action.payload.data;
            state.isLoading[trimActionType(action.type)] = false;
        },
        [handleInvite.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [handleInvite.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
        },
        [sendResetEmail.fulfilled]: (state, action) => {
            setAlertSuccess(state, 'Email sent!');
            state.isLoading[trimActionType(action.type)] = false;
        },
        [sendResetEmail.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [sendResetEmail.rejected]: (state, action) => {
            setAlertError(state, 'Unable to send email');
            state.isLoading[trimActionType(action.type)] = false;
        },
        [signInUser.fulfilled]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
            if (action.payload.data.hasPasskey === false && action.payload.data.passkeySetupSkipped === false) {
                state.promptPasskeySetup = true;
            }
        },
        [signInUser.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [signInUser.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
            if (action?.error?.message === "MFA_CODE_REQUIRED") {
                setMfaType(state, { payload: action.error.mfaType });
                setMfaRecipient(state, { payload: action.error.recipient });
                state.mfa.hasPasskey = action.error.hasPasskey || false;
                state.mfa.hasTotp = action.error.hasTotp || false;
                state.mfa.hasEmail = action.error.hasEmail || false;
            }
        },
        [signOutUser.fulfilled]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
            resetMfaState(state);
        },
        [signOutUser.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [signOutUser.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
        },
        [verifyMFA.fulfilled]: (state, action) => {
            const result = action.payload?.data;
            if (!result?.success) setAlertError(state, 'Invalid MFA code, please retry');
            state.isLoading[trimActionType(action.type)] = false;
        },
        [verifyMFA.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [verifyMFA.rejected]: (state, action) => {
            setAlertError(state, 'Unable to verify MFA');
            state.isLoading[trimActionType(action.type)] = false;
        },
        [getAvailableMfaTypes.fulfilled]: (state, action) => {
            state.mfa.availableTypes = action.payload.data.availableTypes;
            state.isLoading[trimActionType(action.type)] = false;
        },
        [getAvailableMfaTypes.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [getAvailableMfaTypes.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
        },
    },
});

export const authActions = {
    ...AuthSlice.actions,
    forceChangePassword,
    getCurrentTenantByCode,
    enrollMFA,
    getAvailableMfaTypes,
    enrollPasskey,
    verifyPasskeyEnrollment,
    generateAuthenticationOptions,
    verifyAuthentication,
    skipPasskeySetup,
    getRedirect,
    handleInvite,
    signInUser,
    signOutUser,
    sendResetEmail,
    changePassword,
    recordPasswordEvent,
    verifyMFA,
    getMfaAvailability,
    getMfaSettings,
    verifyMfaForSettings,
    sendEmailMfaForSettings,
    removeTOTP,
    removePasskey,
    verifySamlMfa,
    removeEmailMFA,
};

export default AuthSlice.reducer;
